import { ActionConfirmation } from "./ui/ActionConfirmation.js";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  DEFAULT_STUDENT_ACCESSIBILITY_PREFERENCES,
  STUDENT_ACCESSIBILITY_FONT_FAMILIES,
  STUDENT_ACCESSIBILITY_FONT_SIZES,
  STUDENT_ACCESSIBILITY_TEXT_SPACING,
  type StudentAccessibilityPreferences,
} from "./studentAccessibilityApi.js";
import {
  STUDENT_ACCESSIBILITY_PALETTES,
  currentStudentDefaultColours,
  effectiveStudentColours,
  studentAccessibilityColoursAreReadable,
  studentAccessibilityContrast,
  studentAccessibilityStyle,
} from "./studentAccessibilityTheme.js";

export interface AccessibilityEditorProps {
  preferences: StudentAccessibilityPreferences;
  loading?: boolean;
  loadError?: string | null;
  onSave: (preferences: StudentAccessibilityPreferences) => Promise<StudentAccessibilityPreferences>;
  onReset: () => Promise<StudentAccessibilityPreferences>;
  onApplied: (preferences: StudentAccessibilityPreferences) => void;
  onReload?: () => void;
}

export function sameAccessibilityPreferences(
  first: StudentAccessibilityPreferences,
  second: StudentAccessibilityPreferences,
): boolean {
  return (
    first.fontSize === second.fontSize &&
    first.fontFamily === second.fontFamily &&
    first.textSpacing === second.textSpacing &&
    first.backgroundColour === second.backgroundColour &&
    first.textColour === second.textColour
  );
}

function fontSizeLabel(value: StudentAccessibilityPreferences["fontSize"]): string {
  if (value === "large") return "Large";
  if (value === "extra-large") return "Extra large";
  return "Default";
}

function fontFamilyLabel(value: StudentAccessibilityPreferences["fontFamily"]): string {
  if (value === "sans-readable") return "Clear sans serif";
  if (value === "serif-readable") return "Clear serif";
  return "Default";
}

function spacingLabel(value: StudentAccessibilityPreferences["textSpacing"]): string {
  if (value === "increased") return "Increased";
  if (value === "extra") return "Extra";
  return "Default";
}

/**
 * Shared editor for student and staff display preferences. Persistence and the
 * effective shell are deliberately owned by the caller: this component only
 * edits, validates, previews and reports the result it was given.
 */
export function AccessibilityEditor({
  preferences,
  loading = false,
  loadError = null,
  onSave,
  onReset,
  onApplied,
  onReload,
}: AccessibilityEditorProps) {
  const [draft, setDraft] = useState<StudentAccessibilityPreferences>(preferences);
  const [busy, setBusy] = useState<"save" | "reset" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // A successful save is echoed by the shell as a fresh preferences object.
  // Keep the confirmation visible across that echo; clear it on the next edit.
  useEffect(() => {
    setDraft(preferences);
    setError(null);
  }, [preferences]);

  const readable = studentAccessibilityColoursAreReadable(draft);
  const contrast = studentAccessibilityContrast(draft);
  const colours = effectiveStudentColours(draft);
  const dirty = !sameAccessibilityPreferences(draft, preferences);
  const unavailable = loading || loadError !== null;

  const selectedPalette = useMemo(
    () =>
      STUDENT_ACCESSIBILITY_PALETTES.find(
        (palette) =>
          palette.backgroundColour.toLowerCase() === colours.backgroundColour.toLowerCase() &&
          palette.textColour.toLowerCase() === colours.textColour.toLowerCase(),
      )?.id ?? null,
    [colours.backgroundColour, colours.textColour],
  );

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!readable || unavailable || busy) return;

    setBusy("save");
    setError(null);
    setSaved(false);
    try {
      const result = await onSave(draft);
      setDraft(result);
      onApplied(result);
      setSaved(true);
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Could not save these settings.");
    } finally {
      setBusy(null);
    }
  }

  async function reset() {
    if (unavailable || busy) return;

    setBusy("reset");
    setError(null);
    setSaved(false);
    try {
      const result = await onReset();
      setDraft(result);
      onApplied(result);
      setSaved(true);
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Could not reset these settings.");
    } finally {
      setBusy(null);
    }
  }

  function usePalette(backgroundColour: string, textColour: string) {
    setDraft((current) => ({ ...current, backgroundColour, textColour }));
    setSaved(false);
  }

  function useDefaultColours() {
    setDraft((current) => ({ ...current, backgroundColour: "default", textColour: "default" }));
    setSaved(false);
  }

  function changeCustomColour(which: "backgroundColour" | "textColour", value: string) {
    const defaults = currentStudentDefaultColours();
    setDraft((current) => ({
      ...current,
      backgroundColour:
        which === "backgroundColour"
          ? value
          : current.backgroundColour === "default"
            ? defaults.backgroundColour
            : current.backgroundColour,
      textColour:
        which === "textColour"
          ? value
          : current.textColour === "default"
            ? defaults.textColour
            : current.textColour,
    }));
    setSaved(false);
  }

  return (
    <>
      {loading && <div className="notice small">Loading display settings…</div>}
      {loadError && (
        <div className="error small" role="alert">
          Could not load these display settings. The Hub is using its normal appearance for now.{" "}
          {onReload ? (
            <button type="button" className="link" onClick={onReload}>
              Try again
            </button>
          ) : null}
        </div>
      )}
      {error && (
        <div className="error small" role="alert">
          {error}
        </div>
      )}
      {saved && (
        <ActionConfirmation variant="success" title="Display settings saved" />
      )}

      <form onSubmit={save}>
        <div className="panel accessibility-settings">
          <h2>Reading and text</h2>
          <div className="fields accessibility-fields">
            <label>
              <span className="small muted">Text size</span>
              <select
                aria-label="Text size"
                value={draft.fontSize}
                disabled={unavailable || busy !== null}
                onChange={(event) => {
                  setDraft((current) => ({
                    ...current,
                    fontSize: event.target.value as StudentAccessibilityPreferences["fontSize"],
                  }));
                  setSaved(false);
                }}
              >
                {STUDENT_ACCESSIBILITY_FONT_SIZES.map((value) => (
                  <option key={value} value={value}>
                    {fontSizeLabel(value)}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span className="small muted">Font</span>
              <select
                aria-label="Font"
                value={draft.fontFamily}
                disabled={unavailable || busy !== null}
                onChange={(event) => {
                  setDraft((current) => ({
                    ...current,
                    fontFamily: event.target.value as StudentAccessibilityPreferences["fontFamily"],
                  }));
                  setSaved(false);
                }}
              >
                {STUDENT_ACCESSIBILITY_FONT_FAMILIES.map((value) => (
                  <option key={value} value={value}>
                    {fontFamilyLabel(value)}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span className="small muted">Text spacing</span>
              <select
                aria-label="Text spacing"
                value={draft.textSpacing}
                disabled={unavailable || busy !== null}
                onChange={(event) => {
                  setDraft((current) => ({
                    ...current,
                    textSpacing: event.target.value as StudentAccessibilityPreferences["textSpacing"],
                  }));
                  setSaved(false);
                }}
              >
                {STUDENT_ACCESSIBILITY_TEXT_SPACING.map((value) => (
                  <option key={value} value={value}>
                    {spacingLabel(value)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="panel accessibility-settings">
          <h2>Colours</h2>
          <p className="small muted">
            Choose a starting combination or use the colour pickers. The Hub will not save a pair
            that makes ordinary text too difficult to distinguish from its background.
          </p>

          <div className="accessibility-palettes" role="group" aria-label="Colour combinations">
            <button
              type="button"
              className={draft.backgroundColour === "default" && draft.textColour === "default" ? "" : "quiet"}
              aria-pressed={draft.backgroundColour === "default" && draft.textColour === "default"}
              disabled={unavailable || busy !== null}
              onClick={useDefaultColours}
            >
              Default
            </button>
            {STUDENT_ACCESSIBILITY_PALETTES.map((palette) => (
              <button
                key={palette.id}
                type="button"
                className={selectedPalette === palette.id ? "" : "quiet"}
                aria-pressed={selectedPalette === palette.id}
                disabled={unavailable || busy !== null}
                onClick={() => usePalette(palette.backgroundColour, palette.textColour)}
              >
                <span
                  className="accessibility-swatch"
                  style={{ background: palette.backgroundColour, color: palette.textColour }}
                  aria-hidden="true"
                >
                  Aa
                </span>
                {palette.label}
              </button>
            ))}
          </div>

          <div className="fields accessibility-colour-fields">
            <label>
              <span className="small muted">Background colour</span>
              <input
                aria-label="Background colour"
                type="color"
                value={colours.backgroundColour}
                disabled={unavailable || busy !== null}
                onChange={(event) => changeCustomColour("backgroundColour", event.target.value)}
              />
            </label>
            <label>
              <span className="small muted">Text colour</span>
              <input
                aria-label="Text colour"
                type="color"
                value={colours.textColour}
                disabled={unavailable || busy !== null}
                onChange={(event) => changeCustomColour("textColour", event.target.value)}
              />
            </label>
          </div>

          {!readable ? (
            <div className="error small" role="alert">
              Choose colours with more contrast before saving. This pair is {contrast.toFixed(1)}:1.
            </div>
          ) : (
            <p className="small muted accessibility-contrast" aria-live="polite">
              Text/background contrast: {contrast.toFixed(1)}:1.
            </p>
          )}
        </div>

        <div
          className="panel accessibility-preview"
          style={studentAccessibilityStyle(draft)}
          aria-label="Display settings preview"
        >
          <h2>Preview</h2>
          <p>
            This is an example of ordinary lesson information. Use the controls above until it is
            comfortable to read.
          </p>
          <p className="small muted">Muted supporting text stays readable too.</p>
          <p>
            <a href="#preview-link" onClick={(event) => event.preventDefault()}>
              Example link
            </a>
          </p>
          <button type="button" onClick={() => undefined}>
            Example button
          </button>
        </div>

        <div className="row accessibility-actions">
          <button type="submit" disabled={unavailable || busy !== null || !readable || !dirty}>
            {busy === "save" ? "Saving…" : "Save display settings"}
          </button>
          <button
            type="button"
            className="quiet"
            disabled={
              unavailable ||
              busy !== null ||
              sameAccessibilityPreferences(preferences, DEFAULT_STUDENT_ACCESSIBILITY_PREFERENCES)
            }
            onClick={reset}
          >
            {busy === "reset" ? "Resetting…" : "Reset to defaults"}
          </button>
        </div>
      </form>
    </>
  );
}
